import { backOff } from "exponential-backoff"

import ENV from "@/env"

export type TimeoutConfig = {
  maxTries: number
  initialDelay: number
}

export const NORMAL_OPERATION: TimeoutConfig = {
  maxTries: 1000,
  initialDelay: 2_000,
}

export function wait(time: number): Promise<null> {
  return new Promise((resolve) => setTimeout(() => resolve(null), time))
}

export function timeout(time: number) {
  return new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), time))
}

/**
 * Returns if this is a valid arweave address
 */
export const isAddress = (addr: string) => /[a-z0-9_-]{43}/i.test(addr)

export const calcPercentChange = (previousPrice?: number, latestPrice?: number) => {
  if (!previousPrice || !latestPrice || previousPrice === 0 || latestPrice === 0) {
    return 0
  }

  return ((latestPrice - previousPrice) / previousPrice) * 100
}

export const isBrowser = typeof window !== "undefined"
export const isStaging = isBrowser && window.location.toString().includes("staging")
export const isDevelopment = isBrowser && window.location.toString().includes("localhost")
export const showExperimentalFeatures =
  isStaging ||
  isDevelopment ||
  ENV.VITE_ENV === "staging" ||
  (isBrowser && window.location.toString().includes("experimental"))

export class BackendError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "BackendError"
  }
}

// `T` is a generic type for the return type of the function.
// `Args` is a tuple type representing the argument types of the function `fn`.
export function retryable<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>, // Function to be retried
  timeoutConfig = NORMAL_OPERATION,
): (...args: Args) => Promise<T> {
  const { maxTries, initialDelay } = timeoutConfig

  // Return a new function that wraps the original function
  return async (...args: Args): Promise<T> =>
    backOff(() => fn(...args), {
      delayFirstAttempt: initialDelay > 0,
      startingDelay: initialDelay,
      timeMultiple: 1.1, // Default is `2`
      numOfAttempts: maxTries, // Maximum number of retry attempts
      retry: (error, attemptNumber) => {
        console.warn("onRetryFailed", attemptNumber, error)
        if (error instanceof BackendError) {
          return false // Stop retrying if it's a BackendError
        }
        return true // Continue retrying if there's an error
      },
    })
}
